// src/pages/WritersPage.js - COMPLETE WITH UNIQUE CLASS NAMES
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  atomicWriterRegistration,
  checkUsername, 
  checkEmail, 
  initializeCSRF 
} from "../api/api";
import { 
  FaCheck, FaTimes, FaEye, FaEyeSlash, FaUserEdit, FaLock, 
  FaEnvelope, FaUser, FaSignInAlt, FaTimesCircle, FaArrowLeft,
  FaUserCircle, FaFileAlt, FaClipboardCheck, FaSpinner, FaGlobe,
  FaPhone, FaMapMarkerAlt, FaBriefcase, FaGraduationCap, FaPenAlt,
  FaCalendar, FaClock, FaLanguage, FaBook, FaStar, FaMoneyBill, FaKey,
  FaHourglassHalf, FaUserCheck, FaExclamationTriangle, FaBug
} from "react-icons/fa";
import WritersLoginModal from "../components/WritersLoginModal";
import "./WritersPage.css";

const WritersPage = () => {
  const navigate = useNavigate();

  // Registration state
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationData, setRegistrationData] = useState({
    step1: { username: "", email: "", password: "", confirmPassword: "" },
    step2: {
      first_name: "", last_name: "", phone_number: "", country: "",
      city: "", address: "", professional_title: "", bio: "",
      years_of_experience: 0, specialties: [], linkedin_url: "",
      portfolio_url: "", website_url: ""
    },
    step3: {
      highest_degree: "", field_of_study: "", university: "", graduation_year: "",
      writing_experience: "", previous_platforms: "", preferred_subjects: [],
      preferred_academic_levels: [], preferred_languages: [], hours_per_week: 20,
      preferred_deadline_type: "FLEXIBLE"
    }
  });

  // Step 1 states
  const [showPassword, setShowPassword] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });
  
  // Common states
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");
  const [showDebug, setShowDebug] = useState(false);

  // Multi-select options
  const academicLevelOptions = [
    { value: 'High School', label: 'High School' },
    { value: 'Undergraduate', label: 'Undergraduate' },
    { value: 'Master\'s', label: 'Master\'s' },
    { value: 'PhD/Doctoral', label: 'PhD/Doctoral' }
  ];

  const languageOptions = [
    { value: 'English', label: 'English' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'French', label: 'French' },
    { value: 'German', label: 'German' },
    { value: 'Other', label: 'Other' }
  ];

  // Initialize CSRF
  useEffect(() => {
    initializeCSRF().catch(err => {
      console.error("CSRF initialization failed:", err);
      setGeneralError("Failed to initialize security. Please refresh the page.");
    });
  }, []);

  // Check username availability (Step 1)
  useEffect(() => {
    const username = registrationData.step1.username.trim();
    const timer = setTimeout(async () => {
      if (username !== "") {
        try {
          const res = await checkUsername(username);
          setUsernameAvailable(res.available);
          if (!res.available) {
            setErrors(prev => ({ ...prev, username: "Username is already taken" }));
          }
        } catch (err) {
          setUsernameAvailable(false);
          if (err.username || err.error) {
            setErrors(prev => ({ ...prev, username: err.username || err.error }));
          }
        }
      } else {
        setUsernameAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [registrationData.step1.username]);

  // Check email availability (Step 1)
  useEffect(() => {
    const email = registrationData.step1.email.trim();
    const timer = setTimeout(async () => {
      if (email !== "") {
        try {
          const res = await checkEmail(email);
          setEmailAvailable(res.available);
          if (!res.available) {
            setErrors(prev => ({ ...prev, email: "Email is already registered" }));
          }
        } catch (err) {
          setEmailAvailable(false);
          if (err.email || err.error) {
            setErrors(prev => ({ ...prev, email: err.email || err.error }));
          }
        }
      } else {
        setEmailAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [registrationData.step1.email]);

  // Validate password requirements (Step 1)
  useEffect(() => {
    const password = registrationData.step1.password;
    setPasswordValidations({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*]/.test(password),
    });
  }, [registrationData.step1.password]);

  // Handle input changes
  const handleChange = (step, field, value) => {
    setRegistrationData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [field]: value
      }
    }));
    
    // Clear errors for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
    if (generalError) {
      setGeneralError("");
    }
  };

  // Handle specialty tags (Step 2)
  const handleSpecialtyChange = (e) => {
    const value = e.target.value;
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      const newSpecialties = [...registrationData.step2.specialties, value.trim()];
      handleChange('step2', 'specialties', newSpecialties);
      e.target.value = '';
    }
  };

  const removeSpecialty = (index) => {
    const newSpecialties = registrationData.step2.specialties.filter((_, i) => i !== index);
    handleChange('step2', 'specialties', newSpecialties);
  };

  // Handle academic level selection
  const handleAcademicLevelChange = (level, isChecked) => {
    const currentLevels = [...registrationData.step3.preferred_academic_levels];
    
    if (isChecked) {
      if (!currentLevels.includes(level)) {
        currentLevels.push(level);
      }
    } else {
      const index = currentLevels.indexOf(level);
      if (index > -1) {
        currentLevels.splice(index, 1);
      }
    }
    
    handleChange('step3', 'preferred_academic_levels', currentLevels);
  };

  // Handle language selection
  const handleLanguageChange = (language, isChecked) => {
    const currentLanguages = [...registrationData.step3.preferred_languages];
    
    if (isChecked) {
      if (!currentLanguages.includes(language)) {
        currentLanguages.push(language);
      }
    } else {
      const index = currentLanguages.indexOf(language);
      if (index > -1) {
        currentLanguages.splice(index, 1);
      }
    }
    
    handleChange('step3', 'preferred_languages', currentLanguages);
  };

  // Handle other array fields (subjects)
  const handleArrayField = (step, field, value) => {
    const arrayValue = value.split(',').map(item => item.trim()).filter(item => item);
    handleChange(step, field, arrayValue);
  };

  // Validate current step
  const validateCurrentStep = () => {
    const validationErrors = {};
    
    if (currentStep === 1) {
      const data = registrationData.step1;
      
      if (!data.username.trim()) validationErrors.username = "Username is required";
      else if (usernameAvailable === false) validationErrors.username = "Username is not available";
      else if (usernameAvailable === null && data.username.trim()) validationErrors.username = "Checking username...";

      if (!data.email.trim()) validationErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(data.email)) validationErrors.email = "Email is invalid";
      else if (emailAvailable === false) validationErrors.email = "Email is not available";
      else if (emailAvailable === null && data.email.trim()) validationErrors.email = "Checking email...";

      if (!data.password) validationErrors.password = "Password is required";
      else if (!passwordValidations.length || !passwordValidations.uppercase || 
               !passwordValidations.number || !passwordValidations.specialChar) {
        validationErrors.password = "Password does not meet all requirements";
      }

      if (data.password !== data.confirmPassword) {
        validationErrors.confirmPassword = "Passwords do not match";
      }

      // Check terms
      const termsCheckbox = document.getElementById("writerpage-terms");
      if (!termsCheckbox?.checked) {
        validationErrors.terms = "You must accept the Terms & Conditions and Privacy Policy";
      }
    }
    
    else if (currentStep === 2) {
      const data = registrationData.step2;
      
      // Required fields validation
      const requiredFields = ['first_name', 'last_name', 'phone_number', 'country'];
      requiredFields.forEach(field => {
        if (!data[field] || !data[field].toString().trim()) {
          validationErrors[field] = `${field.replace('_', ' ')} is required`;
        }
      });
      
      // Phone validation
      if (data.phone_number && !/^[\d\s\+\-\(\)]{10,20}$/.test(data.phone_number)) {
        validationErrors.phone_number = "Enter a valid phone number";
      }
    }
    
    else if (currentStep === 3) {
      const data = registrationData.step3;
      
      // Required fields validation
      const requiredFields = ['highest_degree', 'field_of_study', 'writing_experience'];
      requiredFields.forEach(field => {
        if (!data[field] || !data[field].toString().trim()) {
          validationErrors[field] = `${field.replace('_', ' ')} is required`;
        }
      });
      
      // Validate academic levels
      if (data.preferred_academic_levels.length === 0) {
        validationErrors.academic_levels = "Select at least one academic level";
      }
      
      // Validate languages
      if (data.preferred_languages.length === 0) {
        validationErrors.languages = "Select at least one language";
      }
    }
    
    return validationErrors;
  };

  // Go to next step
  const goToNextStep = () => {
    const validationErrors = validateCurrentStep();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      return;
    }
    
    // Clear errors
    setErrors({});
    setGeneralError("");
    
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // On step 3, we'll submit all
      handleSubmitAllSteps();
    }
  };

  // Go to previous step
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
      setGeneralError("");
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle final submission of all steps WITH DEBUGGING
  const handleSubmitAllSteps = async () => {
    console.log("🛠️ === DEBUG START ===");
    
    // Validate all steps first
    const step1Errors = validateCurrentStep();
    const step2Errors = validateCurrentStep();
    const step3Errors = validateCurrentStep();
    
    const allErrors = { ...step1Errors, ...step2Errors, ...step3Errors };
    
    console.log("🔍 Validation errors:", allErrors);
    
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      
      // Scroll to first error
      const firstErrorField = Object.keys(allErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      console.log("❌ Validation failed, not submitting");
      console.log("🛠️ === DEBUG END ===");
      return;
    }
    
    setIsLoading(true);
    setGeneralError("");
    setErrors({});
    
    try {
      // Prepare data for atomic submission - VERBOSE DEBUGGING
      const step1Data = {
        username: registrationData.step1.username.trim(),
        email: registrationData.step1.email.trim(),
        password: registrationData.step1.password
      };
      
      const step2Data = {
        first_name: registrationData.step2.first_name,
        last_name: registrationData.step2.last_name,
        phone_number: registrationData.step2.phone_number,
        country: registrationData.step2.country,
        city: registrationData.step2.city || '',
        address: registrationData.step2.address || '',
        professional_title: registrationData.step2.professional_title || '',
        bio: registrationData.step2.bio || '',
        years_of_experience: parseInt(registrationData.step2.years_of_experience) || 0,
        specialties: Array.isArray(registrationData.step2.specialties) 
          ? registrationData.step2.specialties 
          : [],
        linkedin_url: registrationData.step2.linkedin_url || '',
        portfolio_url: registrationData.step2.portfolio_url || '',
        website_url: registrationData.step2.website_url || ''
      };
      
      const step3Data = {
        highest_degree: registrationData.step3.highest_degree,
        field_of_study: registrationData.step3.field_of_study,
        university: registrationData.step3.university || '',
        graduation_year: registrationData.step3.graduation_year 
          ? parseInt(registrationData.step3.graduation_year) 
          : null,
        writing_experience: registrationData.step3.writing_experience,
        previous_platforms: registrationData.step3.previous_platforms || '',
        preferred_subjects: Array.isArray(registrationData.step3.preferred_subjects) 
          ? registrationData.step3.preferred_subjects 
          : (registrationData.step3.preferred_subjects || '').split(',').map(s => s.trim()).filter(s => s),
        preferred_academic_levels: Array.isArray(registrationData.step3.preferred_academic_levels) 
          ? registrationData.step3.preferred_academic_levels 
          : [],
        preferred_languages: Array.isArray(registrationData.step3.preferred_languages) 
          ? registrationData.step3.preferred_languages 
          : [],
        hours_per_week: parseInt(registrationData.step3.hours_per_week) || 20,
        preferred_deadline_type: registrationData.step3.preferred_deadline_type || 'FLEXIBLE'
      };
      
      console.log("🔍 === STEP DATA TO SEND ===");
      console.log("Step 1 data:", { ...step1Data, password: "[HIDDEN]" });
      console.log("Step 2 data:", step2Data);
      console.log("Step 3 data:", step3Data);
      
      // Log the full payload structure
      const debugPayload = {
        step1: step1Data,
        step2: step2Data,
        step3: step3Data
      };
      
      console.log("📤 Full payload structure:", debugPayload);
      
      // Set debug info for display
      setDebugInfo(JSON.stringify({
        timestamp: new Date().toISOString(),
        step1: { ...step1Data, password: "[HIDDEN]" },
        step2: step2Data,
        step3: step3Data
      }, null, 2));
      
      console.log("🚀 Calling atomicWriterRegistration API...");
      
      const result = await atomicWriterRegistration(step1Data, step2Data, step3Data);
      
      console.log("✅ API Response:", result);
      
      if (result.success) {
        setRegistrationComplete(true);
        setAccountStatus(result.profile_status?.account_status || 'UNDER_REVIEW');
        setSuccessMessage(result.message);
        setCurrentStep('complete');
        
        // Save auth data if provided
        if (result.token) {
          localStorage.setItem('token', result.token);
          console.log("🔑 Token saved to localStorage");
        }
        if (result.user) {
          localStorage.setItem('writer_user', JSON.stringify(result.user));
          console.log("👤 User data saved to localStorage");
        }
        
        // Clear form data
        setRegistrationData({
          step1: { username: "", email: "", password: "", confirmPassword: "" },
          step2: {
            first_name: "", last_name: "", phone_number: "", country: "",
            city: "", address: "", professional_title: "", bio: "",
            years_of_experience: 0, specialties: [], linkedin_url: "",
            portfolio_url: "", website_url: ""
          },
          step3: {
            highest_degree: "", field_of_study: "", university: "", graduation_year: "",
            writing_experience: "", previous_platforms: "", preferred_subjects: [],
            preferred_academic_levels: [], preferred_languages: [], hours_per_week: 20,
            preferred_deadline_type: "FLEXIBLE"
          }
        });
        
        console.log("🎉 Registration successful!");
      } else {
        // Handle backend errors
        console.error("❌ Backend errors:", result.errors);
        
        if (result.errors) {
          const flattenedErrors = {};
          Object.keys(result.errors).forEach(step => {
            Object.keys(result.errors[step]).forEach(field => {
              flattenedErrors[field] = Array.isArray(result.errors[step][field]) 
                ? result.errors[step][field].join(', ') 
                : result.errors[step][field];
            });
          });
          setErrors(flattenedErrors);
          console.error("Flattened errors:", flattenedErrors);
        } else {
          setGeneralError(result.error || "Registration failed");
        }
      }
    } catch (err) {
      console.error("❌ Registration error:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response,
        data: err.response?.data
      });
      
      if (err.errors) {
        // Handle nested errors
        const flattenedErrors = {};
        Object.keys(err.errors).forEach(step => {
          if (typeof err.errors[step] === 'object') {
            Object.keys(err.errors[step]).forEach(field => {
              flattenedErrors[field] = Array.isArray(err.errors[step][field]) 
                ? err.errors[step][field].join(', ') 
                : err.errors[step][field];
            });
          }
        });
        setErrors(flattenedErrors);
      } else if (err.error) {
        setGeneralError(err.error);
      } else if (err.details) {
        setGeneralError(err.details);
      } else {
        setGeneralError("Registration failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
      console.log("🛠️ === DEBUG END ===");
    }
  };

  // Reset form for new registration
  const handleResetForm = () => {
    setCurrentStep(1);
    setRegistrationComplete(false);
    setAccountStatus(null);
    setRegistrationData({
      step1: { username: "", email: "", password: "", confirmPassword: "" },
      step2: {
        first_name: "", last_name: "", phone_number: "", country: "",
        city: "", address: "", professional_title: "", bio: "",
        years_of_experience: 0, specialties: [], linkedin_url: "",
        portfolio_url: "", website_url: ""
      },
      step3: {
        highest_degree: "", field_of_study: "", university: "", graduation_year: "",
        writing_experience: "", previous_platforms: "", preferred_subjects: [],
        preferred_academic_levels: [], preferred_languages: [], hours_per_week: 20,
        preferred_deadline_type: "FLEXIBLE"
      }
    });
    setSuccessMessage("");
    setErrors({});
    setGeneralError("");
    setUsernameAvailable(null);
    setEmailAvailable(null);
    setDebugInfo("");
    
    // Clear terms checkbox
    const termsCheckbox = document.getElementById("writerpage-terms");
    if (termsCheckbox) termsCheckbox.checked = false;
  };

  // Modal handlers
  const handleModalOverlayClick = (e, closeFunction) => {
    if (e.target === e.currentTarget) {
      closeFunction();
    }
  };

  // Common Terms and Conditions content
  const termsContent = `
    <h2>Terms and Conditions for Writers</h2>
    <p>By registering as a writer on our platform, you agree to be bound by these Terms and Conditions.</p>
    <h3>1. Writer Responsibilities</h3>
    <p>As a writer, you agree to:</p>
    <ul>
      <li>Produce original, plagiarism-free content</li>
      <li>Meet all deadlines for assigned work</li>
      <li>Maintain professional communication with clients</li>
      <li>Adhere to platform guidelines and policies</li>
    </ul>
    <h3>2. Payment Terms</h3>
    <p>Writers receive 20% of the order value for completed work. Payments are processed within 7 business days of order completion.</p>
    <h3>3. Intellectual Property</h3>
    <p>Upon completion and payment, all rights to the work transfer to the client.</p>
  `;

  // Common Privacy Policy content
  const privacyContent = `
    <h2>Privacy Policy for Writers</h2>
    <p>We collect and use your information as described below.</p>
    <h3>1. Information We Collect</h3>
    <ul>
      <li>Personal information (name, email, phone)</li>
      <li>Professional information (education, experience)</li>
      <li>Payment information for payouts</li>
      <li>Writing samples and portfolio</li>
    </ul>
    <h3>2. How We Use Your Information</h3>
    <ul>
      <li>To match you with suitable writing projects</li>
      <li>To process payments</li>
      <li>To communicate about platform updates</li>
      <li>To improve our services</li>
    </ul>
  `;

  // ========== RENDER STEP CONTENT ==========

  const renderStep1 = () => (
    <form onSubmit={(e) => { e.preventDefault(); goToNextStep(); }} noValidate className="writerpage-form">
      <div className="writerpage-step-header">
        <h2>Step 1: Create Your Account</h2>
        <p>Start by creating your writer account with basic credentials</p>
      </div>

      {/* Username Field */}
      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaUser className="writerpage-input-icon" />
          Username *
        </label>
        <input
          type="text"
          name="username"
          className={`writerpage-input ${errors.username ? 'writerpage-error' : ''} ${usernameAvailable ? 'writerpage-success' : ''}`}
          value={registrationData.step1.username}
          onChange={(e) => handleChange('step1', 'username', e.target.value)}
          disabled={isLoading}
          placeholder="Choose a unique username"
          required
        />
        <div className="writerpage-input-feedback">
          {registrationData.step1.username && usernameAvailable !== null && (
            <span className={usernameAvailable ? "writerpage-success-text" : "writerpage-error-text"}>
              {usernameAvailable ? "✓ Username is available" : "✗ Username is taken"}
            </span>
          )}
          {errors.username && (
            <span className="writerpage-error-text">{errors.username}</span>
          )}
        </div>
      </div>

      {/* Email Field */}
      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaEnvelope className="writerpage-input-icon" />
          Email Address *
        </label>
        <input
          type="email"
          name="email"
          className={`writerpage-input ${errors.email ? 'writerpage-error' : ''} ${emailAvailable ? 'writerpage-success' : ''}`}
          value={registrationData.step1.email}
          onChange={(e) => handleChange('step1', 'email', e.target.value)}
          disabled={isLoading}
          placeholder="Enter your professional email"
          required
        />
        <div className="writerpage-input-feedback">
          {registrationData.step1.email && emailAvailable !== null && (
            <span className={emailAvailable ? "writerpage-success-text" : "writerpage-error-text"}>
              {emailAvailable ? "✓ Email is available" : "✗ Email is registered"}
            </span>
          )}
          {errors.email && (
            <span className="writerpage-error-text">{errors.email}</span>
          )}
        </div>
      </div>

      {/* Password Field */}
      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaLock className="writerpage-input-icon" />
          Password *
        </label>
        <div className="writerpage-password-container">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            className={`writerpage-input ${errors.password ? 'writerpage-error' : ''}`}
            value={registrationData.step1.password}
            onChange={(e) => handleChange('step1', 'password', e.target.value)}
            disabled={isLoading}
            placeholder="Create a strong password"
            required
          />
          <button
            type="button"
            className="writerpage-password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
        {errors.password && (
          <span className="writerpage-error-text">{errors.password}</span>
        )}
        
        {/* Password Requirements */}
        <div className="writerpage-password-requirements">
          <h4>Password Requirements:</h4>
          <div className="writerpage-requirements-grid">
            <div className={`writerpage-requirement ${passwordValidations.length ? 'writerpage-met' : ''}`}>
              {passwordValidations.length ? <FaCheck /> : <FaTimes />}
              <span>At least 8 characters</span>
            </div>
            <div className={`writerpage-requirement ${passwordValidations.uppercase ? 'writerpage-met' : ''}`}>
              {passwordValidations.uppercase ? <FaCheck /> : <FaTimes />}
              <span>One uppercase letter</span>
            </div>
            <div className={`writerpage-requirement ${passwordValidations.number ? 'writerpage-met' : ''}`}>
              {passwordValidations.number ? <FaCheck /> : <FaTimes />}
              <span>One number</span>
            </div>
            <div className={`writerpage-requirement ${passwordValidations.specialChar ? 'writerpage-met' : ''}`}>
              {passwordValidations.specialChar ? <FaCheck /> : <FaTimes />}
              <span>One special character</span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Password Field */}
      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaLock className="writerpage-input-icon" />
          Confirm Password *
        </label>
        <input
          type="password"
          name="confirmPassword"
          className={`writerpage-input ${errors.confirmPassword ? 'writerpage-error' : ''}`}
          value={registrationData.step1.confirmPassword}
          onChange={(e) => handleChange('step1', 'confirmPassword', e.target.value)}
          disabled={isLoading}
          placeholder="Re-enter your password"
          required
        />
        {errors.confirmPassword && (
          <span className="writerpage-error-text">{errors.confirmPassword}</span>
        )}
      </div>

      {/* Terms and Conditions */}
      <div className="writerpage-terms-container">
        <input 
          type="checkbox" 
          id="writerpage-terms" 
          required 
          disabled={isLoading}
        />
        <label htmlFor="writerpage-terms">
          I agree to the{" "}
          <button 
            type="button"
            className="writerpage-terms-link"
            onClick={() => setShowTermsModal(true)}
            disabled={isLoading}
          >
            Terms of Service
          </button>{" "}
          and{" "}
          <button 
            type="button"
            className="writerpage-terms-link"
            onClick={() => setShowPrivacyModal(true)}
            disabled={isLoading}
          >
            Privacy Policy
          </button>
        </label>
        {errors.terms && (
          <span className="writerpage-error-text" style={{display: 'block', marginTop: '8px'}}>
            {errors.terms}
          </span>
        )}
      </div>

      {/* Submit Button */}
      <div className="writerpage-form-buttons">
        <button 
          type="submit" 
          className="writerpage-submit-button"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <FaSpinner className="writerpage-spinner" />
              Validating...
            </>
          ) : (
            <>
              <FaCheck className="writerpage-button-icon" />
              Continue to Step 2
            </>
          )}
        </button>
      </div>

      <div className="writerpage-transaction-note">
        <FaExclamationTriangle className="writerpage-transaction-icon" />
        <p><strong>ACID Guarantee:</strong> Nothing is saved to the database until you complete all 3 steps. Your entire registration will be processed atomically.</p>
      </div>
    </form>
  );

  const renderStep2 = () => (
    <form onSubmit={(e) => { e.preventDefault(); goToNextStep(); }} noValidate className="writerpage-form">
      <div className="writerpage-step-header">
        <h2>Step 2: Complete Your Profile</h2>
        <p>Tell us more about yourself and your professional background</p>
      </div>

      <div className="writerpage-form-row">
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaUserCircle className="writerpage-input-icon" /> First Name *
            </label>
            <input
              type="text"
              name="first_name"
              className={`writerpage-input ${errors.first_name ? 'writerpage-error' : ''}`}
              value={registrationData.step2.first_name}
              onChange={(e) => handleChange('step2', 'first_name', e.target.value)}
              placeholder="Enter your first name"
              required
              disabled={isLoading}
            />
            {errors.first_name && (
              <span className="writerpage-error-text">{errors.first_name}</span>
            )}
          </div>
        </div>
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaUserCircle className="writerpage-input-icon" /> Last Name *
            </label>
            <input
              type="text"
              name="last_name"
              className={`writerpage-input ${errors.last_name ? 'writerpage-error' : ''}`}
              value={registrationData.step2.last_name}
              onChange={(e) => handleChange('step2', 'last_name', e.target.value)}
              placeholder="Enter your last name"
              required
              disabled={isLoading}
            />
            {errors.last_name && (
              <span className="writerpage-error-text">{errors.last_name}</span>
            )}
          </div>
        </div>
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaPhone className="writerpage-input-icon" /> Phone Number *
        </label>
        <input
          type="tel"
          name="phone_number"
          className={`writerpage-input ${errors.phone_number ? 'writerpage-error' : ''}`}
          value={registrationData.step2.phone_number}
          onChange={(e) => handleChange('step2', 'phone_number', e.target.value)}
          placeholder="Enter your phone number (e.g., +254718713840)"
          required
          disabled={isLoading}
        />
        {errors.phone_number && (
          <span className="writerpage-error-text">{errors.phone_number}</span>
        )}
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaGlobe className="writerpage-input-icon" /> Country *
        </label>
        <select
          name="country"
          className={`writerpage-input ${errors.country ? 'writerpage-error' : ''}`}
          value={registrationData.step2.country}
          onChange={(e) => handleChange('step2', 'country', e.target.value)}
          required
          disabled={isLoading}
        >
          <option value="">Select your country</option>
          <option value="Kenya">Kenya</option>
          <option value="USA">United States</option>
          <option value="UK">United Kingdom</option>
          <option value="Canada">Canada</option>
          <option value="Australia">Australia</option>
          <option value="Nigeria">Nigeria</option>
          <option value="South Africa">South Africa</option>
          <option value="Other">Other</option>
        </select>
        {errors.country && (
          <span className="writerpage-error-text">{errors.country}</span>
        )}
      </div>

      <div className="writerpage-form-row">
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaMapMarkerAlt className="writerpage-input-icon" /> City
            </label>
            <input
              type="text"
              className="writerpage-input"
              value={registrationData.step2.city}
              onChange={(e) => handleChange('step2', 'city', e.target.value)}
              placeholder="Enter your city"
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaCalendar className="writerpage-input-icon" /> Years of Experience
            </label>
            <input
              type="number"
              min="0"
              max="50"
              className="writerpage-input"
              value={registrationData.step2.years_of_experience}
              onChange={(e) => handleChange('step2', 'years_of_experience', parseInt(e.target.value) || 0)}
              placeholder="Years of writing experience"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaBriefcase className="writerpage-input-icon" /> Professional Title
        </label>
        <input
          type="text"
          className="writerpage-input"
          value={registrationData.step2.professional_title}
          onChange={(e) => handleChange('step2', 'professional_title', e.target.value)}
          placeholder="e.g., Academic Writer, Content Creator, Editor"
          disabled={isLoading}
        />
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaStar className="writerpage-input-icon" /> Specialties
        </label>
        <input
          type="text"
          className="writerpage-input"
          placeholder="Type a specialty and press Enter"
          onKeyDown={handleSpecialtyChange}
          disabled={isLoading}
        />
        {registrationData.step2.specialties.length > 0 && (
          <div className="writerpage-specialties-tags">
            {registrationData.step2.specialties.map((specialty, index) => (
              <span key={index} className="writerpage-specialty-tag">
                {specialty}
                <button 
                  type="button" 
                  onClick={() => removeSpecialty(index)}
                  disabled={isLoading}
                >
                  <FaTimes />
                </button>
              </span>
            ))}
          </div>
        )}
        <small className="writerpage-form-hint">Press Enter after each specialty</small>
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaPenAlt className="writerpage-input-icon" /> Professional Bio
        </label>
        <textarea
          className="writerpage-textarea"
          value={registrationData.step2.bio}
          onChange={(e) => handleChange('step2', 'bio', e.target.value)}
          placeholder="Briefly describe your writing background, expertise, and any certifications"
          rows="4"
          disabled={isLoading}
        />
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaMapMarkerAlt className="writerpage-input-icon" /> Address
        </label>
        <textarea
          className="writerpage-textarea"
          value={registrationData.step2.address}
          onChange={(e) => handleChange('step2', 'address', e.target.value)}
          placeholder="Your full address (optional)"
          rows="3"
          disabled={isLoading}
        />
      </div>

      <div className="writerpage-form-buttons">
        <button 
          type="button" 
          className="writerpage-back-button"
          onClick={goToPreviousStep}
          disabled={isLoading}
        >
          <FaArrowLeft /> Back to Step 1
        </button>
        <button 
          type="submit" 
          className="writerpage-submit-button"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <FaSpinner className="writerpage-spinner" />
              Validating...
            </>
          ) : (
            <>
              <FaCheck className="writerpage-button-icon" />
              Continue to Step 3
            </>
          )}
        </button>
      </div>
    </form>
  );

  const renderStep3 = () => (
    <form onSubmit={(e) => { e.preventDefault(); goToNextStep(); }} noValidate className="writerpage-form">
      <div className="writerpage-step-header">
        <h2>Step 3: Complete Your Application</h2>
        <p>Tell us about your qualifications and preferences</p>
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaGraduationCap className="writerpage-input-icon" /> Highest Degree *
        </label>
        <select
          name="highest_degree"
          className={`writerpage-input ${errors.highest_degree ? 'writerpage-error' : ''}`}
          value={registrationData.step3.highest_degree}
          onChange={(e) => handleChange('step3', 'highest_degree', e.target.value)}
          required
          disabled={isLoading}
        >
          <option value="">Select highest degree</option>
          <option value="High School">High School</option>
          <option value="Associate">Associate Degree</option>
          <option value="Bachelor">Bachelor's Degree</option>
          <option value="Master">Master's Degree</option>
          <option value="PhD">PhD/Doctorate</option>
          <option value="Other">Other</option>
        </select>
        {errors.highest_degree && (
          <span className="writerpage-error-text">{errors.highest_degree}</span>
        )}
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaBook className="writerpage-input-icon" /> Field of Study *
        </label>
        <input
          type="text"
          name="field_of_study"
          className={`writerpage-input ${errors.field_of_study ? 'writerpage-error' : ''}`}
          value={registrationData.step3.field_of_study}
          onChange={(e) => handleChange('step3', 'field_of_study', e.target.value)}
          placeholder="e.g., Computer Science, Business Administration, Psychology"
          required
          disabled={isLoading}
        />
        {errors.field_of_study && (
          <span className="writerpage-error-text">{errors.field_of_study}</span>
        )}
      </div>

      <div className="writerpage-form-row">
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaGraduationCap className="writerpage-input-icon" /> University
            </label>
            <input
              type="text"
              className="writerpage-input"
              value={registrationData.step3.university}
              onChange={(e) => handleChange('step3', 'university', e.target.value)}
              placeholder="University name"
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaCalendar className="writerpage-input-icon" /> Graduation Year
            </label>
            <input
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              className="writerpage-input"
              value={registrationData.step3.graduation_year || ''}
              onChange={(e) => handleChange('step3', 'graduation_year', e.target.value ? parseInt(e.target.value) : '')}
              placeholder="YYYY"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaPenAlt className="writerpage-input-icon" /> Writing Experience *
        </label>
        <textarea
          name="writing_experience"
          className={`writerpage-textarea ${errors.writing_experience ? 'writerpage-error' : ''}`}
          value={registrationData.step3.writing_experience}
          onChange={(e) => handleChange('step3', 'writing_experience', e.target.value)}
          placeholder="Describe your writing experience, areas of expertise, types of documents you've written (essays, research papers, reports, etc.)"
          rows="5"
          required
          disabled={isLoading}
        />
        {errors.writing_experience && (
          <span className="writerpage-error-text">{errors.writing_experience}</span>
        )}
        <small className="writerpage-form-hint">Be specific about your experience and expertise</small>
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaBriefcase className="writerpage-input-icon" /> Previous Writing Platforms
        </label>
        <textarea
          className="writerpage-textarea"
          value={registrationData.step3.previous_platforms}
          onChange={(e) => handleChange('step3', 'previous_platforms', e.target.value)}
          placeholder="List any other writing platforms you've worked with (optional)"
          rows="3"
          disabled={isLoading}
        />
      </div>

      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaBook className="writerpage-input-icon" /> Preferred Subjects
        </label>
        <input
          type="text"
          className="writerpage-input"
          value={registrationData.step3.preferred_subjects.join(', ')}
          onChange={(e) => handleArrayField('step3', 'preferred_subjects', e.target.value)}
          placeholder="e.g., Computer Science, Business, Psychology, Literature"
          disabled={isLoading}
        />
        <small className="writerpage-form-hint">Separate subjects with commas</small>
      </div>

      {/* Academic Levels */}
      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaGraduationCap className="writerpage-input-icon" /> Preferred Academic Levels *
        </label>
        <div className="writerpage-multi-select-container">
          {academicLevelOptions.map((option) => (
            <div key={option.value} className="writerpage-multi-select-option">
              <input
                type="checkbox"
                id={`writerpage-academic-${option.value}`}
                checked={registrationData.step3.preferred_academic_levels.includes(option.value)}
                onChange={(e) => handleAcademicLevelChange(option.value, e.target.checked)}
                disabled={isLoading}
                className="writerpage-multi-select-checkbox"
              />
              <label 
                htmlFor={`writerpage-academic-${option.value}`}
                className={`writerpage-multi-select-label ${registrationData.step3.preferred_academic_levels.includes(option.value) ? 'writerpage-selected' : ''}`}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
        <small className="writerpage-form-hint">Select all that apply</small>
        {errors.academic_levels && (
          <span className="writerpage-error-text">{errors.academic_levels}</span>
        )}
        <div className="writerpage-selected-values">
          {registrationData.step3.preferred_academic_levels.length > 0 ? (
            <span>Selected: {registrationData.step3.preferred_academic_levels.join(', ')}</span>
          ) : (
            <span className="writerpage-hint-text">No levels selected</span>
          )}
        </div>
      </div>

      {/* Languages */}
      <div className="writerpage-form-group">
        <label className="writerpage-form-label">
          <FaLanguage className="writerpage-input-icon" /> Preferred Languages *
        </label>
        <div className="writerpage-multi-select-container">
          {languageOptions.map((option) => (
            <div key={option.value} className="writerpage-multi-select-option">
              <input
                type="checkbox"
                id={`writerpage-language-${option.value}`}
                checked={registrationData.step3.preferred_languages.includes(option.value)}
                onChange={(e) => handleLanguageChange(option.value, e.target.checked)}
                disabled={isLoading}
                className="writerpage-multi-select-checkbox"
              />
              <label 
                htmlFor={`writerpage-language-${option.value}`}
                className={`writerpage-multi-select-label ${registrationData.step3.preferred_languages.includes(option.value) ? 'writerpage-selected' : ''}`}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
        <small className="writerpage-form-hint">Select all languages you can write in</small>
        {errors.languages && (
          <span className="writerpage-error-text">{errors.languages}</span>
        )}
        <div className="writerpage-selected-values">
          {registrationData.step3.preferred_languages.length > 0 ? (
            <span>Selected: {registrationData.step3.preferred_languages.join(', ')}</span>
          ) : (
            <span className="writerpage-hint-text">No languages selected</span>
          )}
        </div>
      </div>

      <div className="writerpage-form-row">
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaClock className="writerpage-input-icon" /> Hours Available Per Week
            </label>
            <input
              type="number"
              min="1"
              max="168"
              className="writerpage-input"
              value={registrationData.step3.hours_per_week}
              onChange={(e) => handleChange('step3', 'hours_per_week', parseInt(e.target.value) || 20)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="writerpage-form-col">
          <div className="writerpage-form-group">
            <label className="writerpage-form-label">
              <FaCalendar className="writerpage-input-icon" /> Preferred Deadline Type
            </label>
            <select
              className="writerpage-input"
              value={registrationData.step3.preferred_deadline_type}
              onChange={(e) => handleChange('step3', 'preferred_deadline_type', e.target.value)}
              disabled={isLoading}
            >
              <option value="FLEXIBLE">Flexible (Normal)</option>
              <option value="URGENT">Urgent (Express)</option>
              <option value="BOTH">Both</option>
              <option value="NORMAL_ONLY">Normal Only</option>
              <option value="URGENT_ONLY">Urgent Only</option>
            </select>
          </div>
        </div>
      </div>

      <div className="writerpage-form-buttons">
        <button 
          type="button" 
          className="writerpage-back-button"
          onClick={goToPreviousStep}
          disabled={isLoading}
        >
          <FaArrowLeft /> Back to Step 2
        </button>
        <button 
          type="submit" 
          className="writerpage-submit-button"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <FaSpinner className="writerpage-spinner" />
              Submitting All Steps...
            </>
          ) : (
            <>
              <FaClipboardCheck className="writerpage-button-icon" />
              Submit All 3 Steps Atomically
            </>
          )}
        </button>
      </div>

      <div className="writerpage-transaction-note">
        <FaExclamationTriangle className="writerpage-transaction-icon" />
        <p><strong>ACID Transaction:</strong> Your entire registration (Steps 1, 2, and 3) will be processed as a single atomic transaction. Either everything succeeds or nothing is saved to the database.</p>
        <p><strong>Important:</strong> Your application will be reviewed by our admin team. You'll be notified via email once approved.</p>
      </div>
    </form>
  );

  const renderSuccess = () => (
    <div className="writerpage-registration-success">
      <div className="writerpage-success-icon">
        {accountStatus === 'UNDER_REVIEW' ? <FaHourglassHalf /> : <FaUserCheck />}
      </div>
      <h2>Application Submitted Successfully!</h2>
      
      {accountStatus === 'UNDER_REVIEW' ? (
        <>
          <div className="writerpage-success-message">
            <p>Thank you for completing your writer registration!</p>
            <p>Your application is now <strong>under review</strong> by our admin team.</p>
            <p>We'll notify you via email once your application is approved.</p>
          </div>
          
          <div className="writerpage-next-steps">
            <h3>What's Next?</h3>
            <ul>
              <li>Our team will review your application within 24-48 hours</li>
              <li>You'll receive an email notification when approved</li>
              <li>Once approved, you can login and start taking orders</li>
              <li>Check your email for updates</li>
            </ul>
          </div>
          
          <div className="writerpage-writer-benefits-preview">
            <h3>As an Approved Writer, You'll Enjoy:</h3>
            <div className="writerpage-benefits-grid">
              <div className="writerpage-benefit-item">
                <FaMoneyBill className="writerpage-benefit-icon" />
                <span>Competitive Earnings</span>
              </div>
              <div className="writerpage-benefit-item">
                <FaStar className="writerpage-benefit-icon" />
                <span>Build Your Rating & Reputation</span>
              </div>
              <div className="writerpage-benefit-item">
                <FaClock className="writerpage-benefit-icon" />
                <span>Flexible Schedule</span>
              </div>
              <div className="writerpage-benefit-item">
                <FaBook className="writerpage-benefit-icon" />
                <span>Diverse Writing Projects</span>
              </div>
            </div>
          </div>
        </>
      ) : accountStatus === 'APPROVED' ? (
        <>
          <div className="writerpage-success-message">
            <p>Congratulations! Your writer account has been <strong>approved</strong>!</p>
            <p>You can now login and start taking orders.</p>
          </div>
          
          <div className="writerpage-success-actions">
            <button 
              onClick={() => setShowLoginModal(true)}
              className="writerpage-login-action-btn"
            >
              <FaSignInAlt /> Login to Dashboard
            </button>
          </div>
        </>
      ) : null}
      
      <div className="writerpage-success-actions">
        <button 
          onClick={handleResetForm}
          className="writerpage-register-another-btn"
        >
          Register Another Account
        </button>
      </div>
    </div>
  );

  // Debug panel
  const renderDebugPanel = () => (
    showDebug && (
      <div className="writerpage-debug-panel">
        <div className="writerpage-debug-header">
          <h3><FaBug /> Debug Information</h3>
          <button onClick={() => setShowDebug(false)}>Close</button>
        </div>
        <div className="writerpage-debug-content">
          <h4>Current State:</h4>
          <pre>{JSON.stringify({
            currentStep,
            isLoading,
            registrationComplete,
            accountStatus,
            usernameAvailable,
            emailAvailable,
            errors,
            generalError
          }, null, 2)}</pre>
          
          <h4>Registration Data:</h4>
          <pre>{JSON.stringify({
            step1: { ...registrationData.step1, password: "[HIDDEN]", confirmPassword: "[HIDDEN]" },
            step2: registrationData.step2,
            step3: registrationData.step3
          }, null, 2)}</pre>
          
          <h4>Debug Info:</h4>
          <pre>{debugInfo || "No debug info available"}</pre>
        </div>
      </div>
    )
  );

  // ========== MAIN RENDER ==========

  return (
    <div className="writerpage-page-container">
      <div className="writerpage-background-overlay"></div>
      
      <div className="writerpage-form-container">
        {/* Header */}
        <div className="writerpage-header">
          <div className="writerpage-icon-container">
            <FaUserEdit className="writerpage-main-icon" />
          </div>
          {registrationComplete ? (
            <>
              <h1 className="writerpage-title" style={{color: '#28a745'}}>
                {accountStatus === 'UNDER_REVIEW' ? 'Application Submitted!' : 'Registration Complete!'}
              </h1>
              <p className="writerpage-subtitle">
                {accountStatus === 'UNDER_REVIEW' 
                  ? 'Your application is under review' 
                  : 'Welcome to our writer community!'}
              </p>
            </>
          ) : (
            <>
              <h1 className="writerpage-title">Join Our Writer Community</h1>
              <p className="writerpage-subtitle">Start your journey as a professional writer today</p>
            </>
          )}
        </div>

        {/* Debug button */}
        {process.env.NODE_ENV === 'development' && (
          <div className="writerpage-debug-button-container">
            <button 
              className="writerpage-debug-button"
              onClick={() => setShowDebug(!showDebug)}
            >
              <FaBug /> {showDebug ? 'Hide Debug' : 'Show Debug'}
            </button>
          </div>
        )}

        {/* Progress Indicator */}
        {!registrationComplete && (
          <div className="writerpage-progress-container">
            <div className="writerpage-progress-steps">
              <div className={`writerpage-progress-step ${currentStep >= 1 ? 'writerpage-active' : ''}`}>
                <div className="writerpage-step-number">1</div>
                <div className="writerpage-step-label">Account</div>
              </div>
              <div className={`writerpage-progress-step ${currentStep >= 2 ? 'writerpage-active' : ''}`}>
                <div className="writerpage-step-number">2</div>
                <div className="writerpage-step-label">Profile</div>
              </div>
              <div className={`writerpage-progress-step ${currentStep >= 3 ? 'writerpage-active' : ''}`}>
                <div className="writerpage-step-number">3</div>
                <div className="writerpage-step-label">Application</div>
              </div>
            </div>
            <div className="writerpage-progress-bar">
              <div 
                className="writerpage-progress-fill" 
                style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {successMessage && !generalError && (
          <div className="writerpage-success-alert">
            <FaCheck /> {successMessage}
          </div>
        )}

        {generalError && (
          <div className="writerpage-error-alert">
            <FaTimes /> {generalError}
          </div>
        )}

        {/* Main Content */}
        <div className="writerpage-registration-content">
          {registrationComplete ? renderSuccess() : (
            <>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </>
          )}
        </div>

        {/* Login Link (only shown in step 1) */}
        {currentStep === 1 && !registrationComplete && (
          <div className="writerpage-login-link">
            <p>
              Already a writer?{" "}
              <button 
                type="button"
                className="writerpage-login-text"
                onClick={() => setShowLoginModal(true)}
                disabled={isLoading}
              >
                Login to your account
              </button>
            </p>
          </div>
        )}
      </div>

      {/* Debug Panel */}
      {renderDebugPanel()}

      {/* Writers Login Modal */}
      <WritersLoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
      />

      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div 
          className="writerpage-modal-overlay"
          onClick={(e) => handleModalOverlayClick(e, () => setShowTermsModal(false))}
        >
          <div className="writerpage-modal-content">
            <div className="writerpage-modal-header">
              <h2 className="writerpage-modal-title">Terms and Conditions</h2>
              <button 
                className="writerpage-modal-close-btn"
                onClick={() => setShowTermsModal(false)}
                aria-label="Close terms modal"
                disabled={isLoading}
              >
                <FaTimesCircle />
              </button>
            </div>
            <div 
              className="writerpage-modal-body"
              dangerouslySetInnerHTML={{ __html: termsContent }}
            />
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div 
          className="writerpage-modal-overlay"
          onClick={(e) => handleModalOverlayClick(e, () => setShowPrivacyModal(false))}
        >
          <div className="writerpage-modal-content">
            <div className="writerpage-modal-header">
              <h2 className="writerpage-modal-title">Privacy Policy</h2>
              <button 
                className="writerpage-modal-close-btn"
                onClick={() => setShowPrivacyModal(false)}
                aria-label="Close privacy modal"
                disabled={isLoading}
              >
                <FaTimesCircle />
              </button>
            </div>
            <div 
              className="writerpage-modal-body"
              dangerouslySetInnerHTML={{ __html: privacyContent }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WritersPage;
